import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import AddIcon from '@mui/icons-material/Add';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import FilterBuilder, { EMPTY_FILTER } from '../../../../components/filter/filterbuilder';
import CountBar from '../../../../components/charts/countbar';
import PopupMenu from '../../../../components/popupmenu';
import { CalcVisibleSeries } from '../components/responsive-sparkline-grid';

import { GroupsContext } from '../../../../data/groups-service';
import DashboardService from '../../../../data/dashboard-service';
import MetadataService from '../../../../data/metadata-service';
import { ParamsSerializer } from '../../../../data/auth-service';
import { GetISODateFromQuick } from '../../../../libraries/helpers';
import DateRange from './../components/date-range';
import SourceSelector from './../components/source-selector';
import { VerifyDates } from '../../widget';

import { get as _get, debounce as _debounce } from 'lodash';

const useStyles = makeStyles(theme => ({
    root: {
    },
    // editor classes
    editorRoot: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    filterText: {
        marginLeft: 10,
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    label: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '100%',
        justifyContent: 'left',
        textAlign: 'left',
        paddingRight: 5,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 12 }
    : { minW: 3, minH: 5 };

const CHART_HEIGHT = 25;

// don't display until size-me can figure out what our size is
const MC2MtdtCountWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MC2MtdtCountWidget.propTypes = {
    data: PropTypes.object.isRequired,
};

const Widget = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [max, setMax] = useState(0);
    const [errors, setErrors] = useState(0);
    const [limit, setLimit] = useState(0);
    const { cmcs } = useContext(GroupsContext);
    const [start, setStart] = useState(props.data.start);
    const [end, setEnd] = useState();

    const fetch = controller => {
        props.setBusy(true);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        return DashboardService.getMtdtCounts({
            start,
            end,
            filter: props.data.settings.json_filter,
            timeline: true,
            targets: props.data.settings.targets,
            source: props.data.settings.source || ['conn'],
            limit,
        }, controller).catch(err => {
            props.onError(err, props.data.title);
            setErrors(current => {
                props.setBusy(false, current > 2);
                if (current > 2) DashboardService.unsubscribe(props.data.id);
                return current + 1;
            });
            return 'error';
        });
    };

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);
        setData((res || []).sort((a, b) => b.value - a.value));
    };

    const subscribe = () => {
        setErrors(0);
        // truncate the data if we need to
        if (data.length > limit) {
            const d = data.slice(0, limit);
            setData(d);
        }
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const handleGroupClick = d => {
        if (!d) return;

        const cmc = (cmcs || []).find(c => c.name === d.key);
        if (!cmc) return;

        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(props.data.settings.json_filter || EMPTY_FILTER),
            cmc: cmc.uuid,
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    const handleDonutClick = filter => {
        if (!filter) return;

        const cmc = (cmcs || []).find(c => c.name === filter.key);
        if (!cmc) return;

        const p = {
            start,
            end,
            quick: props.data.quick,
            filter: JSON.stringify(props.data.settings.json_filter || EMPTY_FILTER),
            cmc: cmc.uuid,
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    // we only need one debouncer
    const setLimitDebounce = useMemo(() => _debounce(setLimit, 420), []);

    // incoming
    useEffect(() => {
        if (!_get(props.size, 'height', 0)) return;
        setLimitDebounce(Math.min(CalcVisibleSeries(props.size.height, 30, CHART_HEIGHT), 42));
    }, [props.size, setLimitDebounce]);

    useEffect(() => {
        setMax(data.reduce((a, c) => a > c.value ? a : c.value, 0));
    }, [data]);

    useEffect(() => {
        setErrors(0);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, []);

    useEffect(() => {
        if (!limit) return;
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
    }, [props.data.start, props.data.end, props.data.quick, limit]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            {(data || []).map(d =>
                <Grid key={d.key}
                    container
                    spacing={2}
                    direction="row"
                    wrap='nowrap'
                    justifyContent="space-between"
                    style={{ width: '100%', height: CHART_HEIGHT + 25 }}>

                    <Grid item style={{ maxWidth: 200, paddingTop: 0, marginTop: 15, height: CHART_HEIGHT }}>
                        <Tooltip arrow placement="top"
                            key={d.key}
                            title={d.key}>
                            <Link className={classes.label}
                                aria-label={d.key}
                                component="button"
                                onClick={() => handleGroupClick(d)}
                                variant="body2">
                                {d.key}
                            </Link>
                        </Tooltip>
                    </Grid>

                    <Grid style={{ width: 'calc(100% - 200px)', marginTop: 15, height: CHART_HEIGHT }}>
                        <div key={d.key}>
                            <CountBar
                                key={d.key}
                                value={d.value}
                                colorKey={d.key}
                                maxGroup={max}
                                height={CHART_HEIGHT}
                                width={props.size.width ? props.size.width - 200 : 150 /*getSize(d.value)*/}
                                label={d.key}
                                onClick={() => handleDonutClick(d)}/>
                        </div>
                    </Grid>
                </Grid>
            )}
        </div>
    );
};
Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();
    const { cmcs } = useContext(GroupsContext);

    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [fields, setFields] = useState([]);
    const [targets, setTargets] = useState(props.data.settings.targets || []);
    const [source, setSource] = useState(props.data.settings.source || ['conn']);
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        setTitle(e.target.value);
    };

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
                source,
                targets,
            },
        });
    };

    const handleRemoveTarget = t => {
        const i = targets.findIndex(tt => t === tt);
        if (i >= 0) targets.splice(i, 1);
        setTargets([...targets]);
    };

    const handleAddTarget = t => {
        const i = targets.findIndex(tt => t.name === tt);
        if (i < 0) targets.push(t.name);
        setTargets([...targets]);
    };

    const handleRemoveSource = s => {
        const i = source.findIndex(src => s === src);
        if (i >= 0) source.splice(i, 1);
        setSource([...source]);
    };

    const handleAddSource = s => {
        const i = source.findIndex(src => s === src);
        if (i < 0) source.push(s);
        setSource([...source]);
    };

    const fetchFields = async () => {
        const fields = await MetadataService.cacheFields();
        setFields(fields.filter(c => c.indexed && c.type !== 'timestamp'));
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        if ((source || []).length < 1) return false;
        return true;
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <SourceSelector
                                onAdd={handleAddSource}
                                onRemove={handleRemoveSource}
                                sources={source}
                                button={
                                    <Button
                                        aria-label="add source"
                                        title="add source"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon/>}>Source
                                    </Button>
                                }/>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(MetadataService.toEnglish(filter, true))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <PopupMenu
                                onSelect={handleAddTarget}
                                aria-label="Filter by cmc(s)"
                                title="Filter by cmc(s)"
                                items={cmcs}
                                itemkey="uuid"
                                itemTitle="name"
                                button={
                                    <Button
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon/>}>CMC
                                    </Button>
                                }/>
                            {targets.map((t, i) =>
                                <div key={`${t}_i`} className={classes.inlineChips}>
                                    <Chip
                                        color="default"
                                        label={t}
                                        onDelete={() => handleRemoveTarget(t)}
                                        className={classes.chip}/>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="primary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MC2MtdtCountWidget as default,
    DEFAULTS,
};
